import { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  SchoolsWithIntegral,
  SectionData,
  TeacherCountsData,
  TeacherData,
  UsageLocalStorageInterface,
} from "../ReportTypes";
import { Column } from "react-table";
import { formatNumber } from "../../../utils";
import {
  customGradeSort,
  MultiCheckFilter,
} from "../../../shared/DeltaMathTable";
import { DemoMode, demoString } from "../../../utils/demo";
import React from "react";
import { TextFilter, NoFilter } from "../../../shared/table/filters";
import { CondensedCell } from "../../../shared/table/cells";
import UsageSelectionTable from "./UsageSelectionTable";

/**
 * Component is responsible for rendering the selection of data scope - really this should be commong between the Usage and Performance reports.
 * But we can leave that as a To-Do for now.
 */
const UsageDataScope = ({
  schoolsWithIntegral,
  stateData,
  setStateData,
  teacherData,
  sectionData,
}: {
  schoolsWithIntegral: SchoolsWithIntegral[];
  stateData: UsageLocalStorageInterface;
  setStateData: React.Dispatch<
    React.SetStateAction<UsageLocalStorageInterface | null>
  >;
  teacherData: TeacherData[];
  sectionData: SectionData[];
}) => {
  const params = useParams();
  const [teacherTableData, setTeacherTableData] = React.useState();
  const [sectionsTableData, setSectionsTableData] = React.useState();
  const [schoolTableData, setSchoolTableData] = React.useState<any>();
  const [schoolsCount, setSchoolsCount] = React.useState<number>(0);
  const [teacherColumns, setTeacherColumns] = React.useState<Column[]>();
  const [sectionColumns, setSectionColumns] = React.useState<Column[]>();

  // Handle pre-selection of schools
  useEffect(() => {
    if (stateData.selectedSchools !== null) {
      setStateData({
        ...stateData,
        selectedSchools: stateData.selectedSchools,
      });
    } else if (schoolTableData && stateData.selectedSchools === null) {
      const allSchools = Object.create({});
      for (let i = 0; i < schoolsWithIntegral.length; i++) {
        allSchools[i] = true;
      }
      setStateData({
        ...stateData,
        selectedSchools: allSchools,
      });
    }
  }, [schoolTableData, stateData.selectedSchools, schoolsWithIntegral]);

  // Handle pre-selection of teachers
  useEffect(() => {
    if (!stateData.selectedTeachers) {
      setStateData({
        ...stateData,
        selectedTeachers:
          teacherData && teacherData.length === 1 ? { 0: true } : {},
      });
    }
  }, [teacherData, stateData.selectedTeachers]);

  // Handle pre-selection of teachers
  useEffect(() => {
    if (!stateData.selectedSections) {
      setStateData({
        ...stateData,
        selectedSections:
          sectionData && sectionData.length === 1 ? { 0: true } : {},
      });
    }
  }, [sectionData, stateData.selectedSections]);

  useEffect(() => {
    const newTeachData: any = [];
    if (Array.isArray(teacherData) && Array.isArray(schoolsWithIntegral)) {
      teacherData.forEach((teacher) => {
        const school = schoolsWithIntegral.find(
          (school) =>
            // not all schoolids are in the 0th index of schoolPlus[], so get the first id that is not a district id
            teacher.schoolPlus.find((id: string) => id.length > 7) ===
            school.schoolid
        );
        // only show integral
        if (school) {
          newTeachData.push({
            first: demoString({ value: teacher.first, type: "person_first" }),
            last: demoString({ value: teacher.last, type: "person_first" }),
            school:
              school &&
              demoString({ value: school.schoolName, type: "school_name" }),
            assignments: teacher.assignmentCount,
          });
        }
      });
    }
    setTeacherTableData(newTeachData);
  }, [teacherData, schoolsWithIntegral]);

  useEffect(() => {
    if (schoolsWithIntegral && Array.isArray(schoolsWithIntegral)) {
      const theSchoolCounts:
        | ((prevState: never[]) => never[])
        | {
            schoolid: string;
            schoolName: string;
            lowGrade: string;
            highGrade: string;
            theSchoolStudentsCount: number;
          }[] = [];
      schoolsWithIntegral.map((school) => {
        theSchoolCounts.push({
          schoolid: school.schoolid,
          schoolName: demoString({
            value: school.schoolName,
            type: "school_name",
          }),
          lowGrade: school.lowGrade,
          highGrade: school.highGrade,
          theSchoolStudentsCount: school.studentCount,
        });
      });
      setSchoolsCount(theSchoolCounts.length);
      setSchoolTableData(theSchoolCounts);
    }
  }, [schoolsWithIntegral, teacherData]);

  useEffect(() => {
    const newSectionsData: any = [];
    if (Array.isArray(sectionData) && Array.isArray(schoolsWithIntegral)) {
      sectionData.map((section) => {
        const school = schoolsWithIntegral.find(
          (school) =>
            // not all schoolids are in the 0th index of schoolPlus[], so get the first id that is not a district id
            section.schools.find((id: string) => id.length > 7) ===
            school.schoolid
        );

        const teacherArray: string[] = [];
        section.teachers.length &&
          section.teachers.forEach((teacher: { _id: number; name: string }) => {
            if (!teacherArray.includes(teacher.name)) {
              teacherArray.push(teacher.name);
            }
          });

        // mostly a check in case there is no "logsPerYear" value, which would throw an error when we access it
        const yearsWeHaveLogs =
          section["logsPerYear"] && Object.keys(section["logsPerYear"]);
        const totalLogs = 0;
        if (school) {
          newSectionsData.push({
            sectionName: demoString({
              value: section.sectionName,
              type: "section_name",
            }),
            teachers:
              teacherArray.length > 0
                ? teacherArray
                    .map((teacher: string) =>
                      demoString({ value: teacher, type: "person_full" })
                    )
                    .join(", ")
                : "-",
            school:
              school &&
              demoString({ value: school.schoolName, type: "school_name" }),
            // aggregate all logs from all years
            logs: Array.isArray(yearsWeHaveLogs)
              ? yearsWeHaveLogs.reduce(
                  (acc: number, year: any) =>
                    acc + (section.logsPerYear || {})[year],
                  totalLogs
                )
              : 0,
          });
        }
        setSectionsTableData(newSectionsData);
      });
      setSectionsTableData(newSectionsData);
    }
  }, [sectionData, schoolsWithIntegral]);

  useEffect(() => {
    if (Array.isArray(schoolTableData)) {
      for (let i = 0; i < schoolTableData?.length; i++) {
        schoolTableData[i].schoolName = demoString({
          value: schoolTableData[i].schoolName,
          type: "school_name",
        });
      }
      setSchoolTableData(schoolTableData);
    }
  }, [schoolTableData]);

  const schoolColumns = React.useMemo(
    () => [
      {
        Header: "School Name",
        accessor: "schoolName",
        Filter: TextFilter,
        align: "left",
      },
      {
        Header: "Grade Range",
        accessor: "lowGrade",
        Cell: (props: any) => {
          return `${props.row.original.lowGrade} - ${props.row.original.highGrade}`;
        },
        align: "center",
        sortType: customGradeSort,
      },
      {
        Header: "# of Students",
        accessor: "theSchoolStudentsCount",
        align: "center",
        sortDescFirst: true,
      },
    ],
    []
  );

  useEffect(() => {
    const teacherColumnsDef: Array<any> = [
      {
        Header: "First Name",
        accessor: "first",
        Filter: TextFilter,
        align: "left",
      },
      {
        Header: "Last Name",
        accessor: "last",
        Filter: TextFilter,
        align: "left",
      },
      {
        Header: "# of Assignments",
        accessor: "assignments",
        Filter: NoFilter,
        align: "left",
        sortDescFirst: true,
      },
    ];

    const sectionColumnsDef: Array<any> = [
      {
        Header: "Section Name",
        accessor: "sectionName",
        Filter: TextFilter,
        align: "left",
      },
      {
        Header: "Teachers",
        accessor: "teachers",
        Filter: TextFilter,
        align: "left",
        Cell: (props: { value: string }) => (
          <CondensedCell value={props.value} />
        ),
      },
      {
        Header: "Problems Solved",
        Cell: (props: any) => {
          return <div>{formatNumber(props.value)}</div>;
        },
        accessor: "logs",
        Filter: NoFilter,
        align: "left",
        sortDescFirst: true,
      },
    ];
    if (schoolsCount > 1) {
      // only show the column if there's more than 1 school
      teacherColumnsDef.splice(2, 0, {
        Header: "School",
        accessor: "school",
        Filter: MultiCheckFilter,
        filter: "includesSome",
        align: "left",
        Cell: (props: any) => {
          return (
            <DemoMode
              value={props.value}
              type="school_name"
              district_size={schoolsCount}
            />
          );
        },
        minWidth: 300,
      });
      sectionColumnsDef.splice(2, 0, {
        Header: "School",
        accessor: "school",
        Filter: MultiCheckFilter,
        filter: "includesSome",
        align: "left",
        minWidth: 300,
      });
    }
    setTeacherColumns(teacherColumnsDef);
    setSectionColumns(sectionColumnsDef);
  }, [schoolsCount]);

  return (
    <>
      {params.page === "schools" && (
        <UsageSelectionTable
          tableData={schoolTableData}
          columns={schoolColumns}
          stateData={stateData}
          setStateData={setStateData}
          stateDataKey="selectedSchools"
        />
      )}

      {params.page === "teachers" && (
        <UsageSelectionTable
          tableData={teacherTableData ?? []}
          columns={teacherColumns ?? []}
          stateData={stateData}
          setStateData={setStateData}
          stateDataKey="selectedTeachers"
        />
      )}

      {params.page === "sections" && (
        <UsageSelectionTable
          tableData={sectionsTableData ?? []}
          columns={sectionColumns ?? []}
          stateData={stateData}
          setStateData={setStateData}
          stateDataKey="selectedSections"
        />
      )}
    </>
  );
};

export default UsageDataScope;
